/**
 * الثوابت المشتركة بين الخادم والواجهة — مصدر واحد للحقيقة.
 */
export const APP_NAME = 'شركة سار لينك للخدمات اللوجستية';
export const APP_SHORT_NAME = 'سار لينك';

/**
 * ★ بيانات المنشأة الرسمية — مصدر واحد للحقيقة.
 * مأخوذة من السجل التجاري وشهادة التسجيل في ضريبة القيمة المضافة،
 * وتُزرع في الإعدادات عند أول إقلاع لأي تثبيت جديد.
 */
export const COMPANY = {
  nameAr: 'شركة سار لينك للخدمات اللوجستية',
  shortNameAr: 'سار لينك',
  /** رقم السجل التجاري (الرقم الموحد للمنشأة) */
  crNumber: '7050315717',
  /** الرقم الوطني الموحد للسجل التجاري */
  unifiedCrNumber: '7050315717',
  /** الرقم الضريبي (رقم التسجيل في ضريبة القيمة المضافة) */
  vatNumber: '313056083200003',
  /** الرقم المميز للمكلف لدى هيئة الزكاة والضريبة والجمارك */
  taxpayerNumber: '3130560832',
  address: 'سراة عبيدة، حي النور، عمرو ابن تميم، 64722',
  /** تاريخ نفاذ التسجيل في ضريبة القيمة المضافة */
  vatEffectiveFrom: '2025-11-01',
  /** الفترة الضريبية حسب شهادة التسجيل */
  vatPeriod: 'ربع سنوي',
} as const;

/** ★ مطوّر النظام — يظهر في الصفحة الرئيسية وشاشة الدخول و«عن البرنامج». */
export const DEVELOPER = {
  name: 'F.A.T.S. Center (Pvt) Ltd.',
  url: 'https://fats.center',
  label: 'fats.center',
} as const;
export const CURRENCY = 'SAR';
export const CURRENCY_LABEL = 'ر.س';
export const DEFAULT_VAT_RATE = 15;
export const DEFAULT_IQAMA_ALERT_DAYS = 60;
export const HIJRI_LOCALE = 'ar-SA-u-ca-islamic-umalqura';
export const AR_LOCALE = 'ar-SA';

/** ── الأدوار ── */
export const ROLES = ['owner', 'accountant', 'supervisor', 'viewer', 'reports_viewer'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'سوبر أدمن',
  accountant: 'المحاسب',
  supervisor: 'الإداري',
  viewer: 'مطّلع',
  reports_viewer: 'مطّلع على التقارير',
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner:
    'سوبر أدمن — صلاحية كاملة على كل الشاشات، وهو **الوحيد** الذي يضيف ويحذف الإداريين والمستخدمين، ويملك فتح الشهور المعتمدة والإعدادات.',
  accountant: 'إدارة البيانات المالية والرواتب والمصروفات والاعتماد والاستيراد، بلا وصول للإعدادات أو المستخدمين.',
  supervisor:
    'الإداري — يضيف ويعدّل بيانات العمال والدراجات، يدخل السجل اليومي، ويرفع ملفات Excel للاستيراد، بلا وصول للرواتب أو الاعتماد أو الإعدادات.',
  viewer: 'قراءة فقط لكل الشاشات التشغيلية والتقارير بلا أي تعديل.',
  reports_viewer:
    'مطّلع على التقارير — يرى التقارير التشغيلية والمالية والضريبية ويحمّلها (PDF/CSV) فقط. لا يرى أي شاشة أخرى ولا يضيف ولا يعدّل ولا يحذف أي شيء.',
};

/** ── حالات الموظف ── */
export const EMPLOYEE_STATUSES = ['active', 'leave', 'terminated'] as const;
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];
export const EMPLOYEE_STATUS_LABELS: Record<EmployeeStatus, string> = {
  active: 'على رأس العمل',
  leave: 'إجازة',
  terminated: 'منتهية خدمته',
};

/** ── منصات التوصيل ── */
export const DELIVERY_PLATFORMS = ['hungerstation', 'keeta', 'jahez', 'mrsool', 'talabat', 'other'] as const;
export type DeliveryPlatform = (typeof DELIVERY_PLATFORMS)[number];
export const DELIVERY_PLATFORM_LABELS: Record<DeliveryPlatform, string> = {
  hungerstation: 'هنقرستيشن',
  keeta: 'كيتا',
  jahez: 'جاهز',
  mrsool: 'مرسول',
  talabat: 'طلبات',
  other: 'أخرى',
};

/** ── سياسة الوقود ── */
export const FUEL_POLICIES = ['company', 'deducted', 'none'] as const;
export type FuelPolicy = (typeof FUEL_POLICIES)[number];
export const FUEL_POLICY_LABELS: Record<FuelPolicy, string> = {
  company: 'البنزين على الشركة',
  deducted: 'يُخصم البنزين من السائق',
  none: 'لا ينطبق',
};

/** ── حالات الدراجة ── */
export const VEHICLE_STATUSES = ['active', 'maintenance', 'retired'] as const;
export type VehicleStatus = (typeof VEHICLE_STATUSES)[number];
export const VEHICLE_STATUS_LABELS: Record<VehicleStatus, string> = {
  active: 'في الخدمة',
  maintenance: 'في الصيانة',
  retired: 'خارج الخدمة',
};

export const OWNERSHIP_TYPES = ['owned', 'leased'] as const;
export type OwnershipType = (typeof OWNERSHIP_TYPES)[number];
export const OWNERSHIP_LABELS: Record<OwnershipType, string> = {
  owned: 'مملوكة',
  leased: 'مستأجرة',
};

/** ── أنواع المركبات ── */
export const VEHICLE_TYPES = ['motorcycle', 'car', 'truck', 'van'] as const;
export type VehicleType = (typeof VEHICLE_TYPES)[number];
export const VEHICLE_TYPE_LABELS: Record<VehicleType, string> = {
  motorcycle: 'دراجة نارية',
  car: 'سيارة',
  truck: 'شاحنة',
  van: 'شاحنة صغيرة (فان)',
};
export const VEHICLE_TYPE_ICONS: Record<VehicleType, string> = { motorcycle: '🏍️', car: '🚗', truck: '🚚', van: '🚐' };

/** ── حالات مسير الرواتب ── */
export const PAYROLL_STATUSES = ['draft', 'approved', 'paid', 'archived'] as const;
export type PayrollStatus = (typeof PAYROLL_STATUSES)[number];
export const PAYROLL_STATUS_LABELS: Record<PayrollStatus, string> = {
  draft: 'مسودة',
  approved: 'معتمد',
  paid: 'مدفوع',
  archived: 'مؤرشف',
};

/** ── طرق الدفع ── */
export const PAYMENT_METHODS = ['cash', 'bank', 'card', 'credit'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'نقداً',
  bank: 'تحويل بنكي',
  card: 'بطاقة',
  credit: 'آجل',
};

/** ── حالات السنة المالية ── */
export const FISCAL_STATUSES = ['open', 'closing', 'closed'] as const;
export type FiscalStatus = (typeof FISCAL_STATUSES)[number];
export const FISCAL_STATUS_LABELS: Record<FiscalStatus, string> = {
  open: 'مفتوحة',
  closing: 'تحت الإقفال',
  closed: 'مقفلة',
};

/** ── الإجراءات في السجل الرقابي ── */
export const AUDIT_ACTIONS = [
  'create',
  'update',
  'delete',
  'restore',
  'approve',
  'pay',
  'unlock',
  'lock',
  'login',
  'login_failed',
  'logout',
  'seed',
  'backup',
  'fiscal_close',
  'fiscal_reopen',
  'sync',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];
export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  create: 'إنشاء',
  update: 'تعديل',
  delete: 'حذف',
  restore: 'استرجاع',
  approve: 'اعتماد',
  pay: 'تحديد مدفوع',
  unlock: 'فتح شهر معتمد',
  lock: 'قفل شهر',
  login: 'تسجيل دخول',
  login_failed: 'محاولة دخول فاشلة',
  logout: 'تسجيل خروج',
  seed: 'تهيئة بيانات',
  backup: 'نسخة احتياطية',
  fiscal_close: 'إقفال سنة مالية',
  fiscal_reopen: 'إعادة فتح سنة مالية',
  sync: 'مزامنة سجل يومي',
};

/** ── أسماء الكيانات في السجل الرقابي ── */
export const ENTITIES = [
  'user',
  'employee',
  'vehicle',
  'daily_entry',
  'payroll_adjustment',
  'payroll_run',
  'expense',
  'expense_category',
  'supplier',
  'revenue',
  'attachment',
  'setting',
  'monthly_summary',
  'fiscal_year',
  'auth',
  'import',
] as const;
export type EntityName = (typeof ENTITIES)[number];

/** ── حدود التحقق ── */
export const LIMITS = {
  iqamaLength: 10,
  ibanLength: 24,
  maxUploadMb: 10,
  maxPageSize: 200,
  defaultPageSize: 25,
  minPasswordLength: 8,
} as const;

/** ── تصنيفات المصروفات المزروعة مسبقاً (nameAr + slug) ── */
export const SEED_EXPENSE_CATEGORIES: Array<{ nameAr: string; slug: string; children?: Array<{ nameAr: string; slug: string }> }> = [
  { nameAr: 'وقود', slug: 'fuel' },
  { nameAr: 'صيانة وإصلاح', slug: 'maintenance' },
  { nameAr: 'قطع غيار', slug: 'spare-parts' },
  { nameAr: 'تأمين مركبات', slug: 'vehicle-insurance' },
  { nameAr: 'استمارة وفحص دوري', slug: 'registration-inspection' },
  { nameAr: 'أقساط أو إيجار دراجات', slug: 'lease' },
  { nameAr: 'مخالفات مرورية', slug: 'traffic-fines' },
  {
    nameAr: 'رسوم حكومية وعمالية',
    slug: 'government-fees',
    children: [
      { nameAr: 'تجديد إقامات', slug: 'iqama-renewal' },
      { nameAr: 'تأمين طبي', slug: 'medical-insurance' },
      { nameAr: 'تأمينات اجتماعية', slug: 'gosi' },
      { nameAr: 'رخص', slug: 'licenses' },
    ],
  },
  { nameAr: 'سكن ومرافق', slug: 'housing-utilities' },
  { nameAr: 'اتصالات وإنترنت', slug: 'telecom-internet' },
  { nameAr: 'معدات وزي', slug: 'equipment-uniform' },
  { nameAr: 'اشتراكات تقنية', slug: 'tech-subscriptions' },
  { nameAr: 'رواتب إدارية', slug: 'admin-salaries' },
  { nameAr: 'رسوم بنكية وعمولات منصات', slug: 'bank-platform-fees' },
  { nameAr: 'نثرية', slug: 'misc' },
];

/** ── رسائل النظام العربية ── */
export const MESSAGES = {
  authRequired: 'يجب تسجيل الدخول للمتابعة',
  forbidden: 'لا تملك صلاحية تنفيذ هذه العملية',
  invalidCredentials: 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
  accountDisabled: 'الحساب معطّل — راجع سوبر أدمن',
  accountLocked: 'تم قفل الحساب مؤقتاً بسبب محاولات دخول متكررة — حاول لاحقاً',
  sessionExpired: 'انتهت الجلسة — سجّل الدخول من جديد',
  notFound: 'العنصر المطلوب غير موجود',
  validationFailed: 'تحقق من المدخلات — هناك حقول غير صحيحة',
  periodLocked: 'هذا الشهر معتمد ومقفل — لا يمكن تعديل بياناته إلا بفتح رسمي مسجّل',
  runAlreadyApproved: 'الشهر معتمد مسبقاً',
  runNotApproved: 'لا يمكن تنفيذ العملية قبل اعتماد الشهر',
  duplicateIqama: 'رقم الإقامة مسجّل لموظف آخر',
  duplicateCode: 'الرقم الوظيفي مستخدم مسبقاً',
  duplicatePlate: 'رقم اللوحة مسجّل لدراجة أخرى',
  duplicateEmail: 'البريد الإلكتروني مستخدم مسبقاً',
  duplicateEntry: 'يوجد سجل لهذا السائق في نفس التاريخ',
  internalError: 'حدث خطأ غير متوقع — حاول مرة أخرى، وإذا تكرر راجع الدعم',
  saved: 'تم الحفظ بنجاح',
  deleted: 'تم الحذف بنجاح',
  restored: 'تم الاسترجاع بنجاح',
  noOrdersForCost: 'لا توجد طلبات لحساب تكلفة الطلب',
  offlineQueued: 'تم الحفظ محلياً — ستتم المزامنة عند عودة الاتصال',
  mustChangePassword: 'يجب تغيير كلمة المرور الافتراضية قبل استخدام النظام',
} as const;
